/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module media-embed/mediaembedediting
 */

import Plugin from "@ckeditor/ckeditor5-core/src/plugin";

import { modelToViewUrlAttributeConverter } from "./converters";
import MediaEmbedCommand from "./mediaembedcommand";
import MediaRegistry from "./mediaregistry";
import { toMediaWidget, createMediaFigureElement } from "./utils";
import ApiService from "../../../../services/ApiService";
import "../theme/mediaembedediting.css";
const ytList = [];
/**
 * The media embed editing feature.
 *
 * @extends module:core/plugin~Plugin
 */
export default class MediaEmbedEditing extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "MediaEmbedEditing";
  }

  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    let audio = document.createElement('audio');
    let video = document.createElement('video');
    editor.config.define("mediaEmbed", {
      providers: [
        {
          name: "dailymotion",
          url: /^dailymotion\.com\/video\/(\w+)/,
          html: (match) => {
            const id = match[1];

            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; ">' +
              `<iframe src="https://www.dailymotion.com/embed/video/${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" width="480" height="270" allowfullscreen allow="autoplay">' +
              "</iframe>" +
              "</div>"
            );
          },
        },

        {
          name: "spotify",
          url: [
            /^open\.spotify\.com\/(artist\/\w+)/,
            /^open\.spotify\.com\/(album\/\w+)/,
            /^open\.spotify\.com\/(track\/\w+)/,
          ],
          html: (match) => {
            const id = match[1];

            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 126%;">' +
              `<iframe src="https://open.spotify.com/embed/${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" allowtransparency="true" allow="encrypted-media">' +
              "</iframe>" +
              "</div>"
            );
          },
        },
        {
          name: "youtube",
          url: [
            /^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)/,
            /^(?:m\.)?youtube\.com\/v\/([\w-]+)/,
            /^(?:m\.)?youtube\.com\/shorts\/([\w-]+)/,
            /^youtube\.com\/embed\/([\w-]+)/,
            /^youtu\.be\/([\w-]+)/,
          ],
          html: (match) => {
            const id = match[1];
            const url = match["input"];
            let ratio = 56.2493;
            if(url.indexOf("width") > 0 && url.indexOf("height") > 0){
              // Regex lấy width và height
              const regex = /[?&]width=(\d+)|[?&]height=(\d+)/g;
              let matchWidhHeight;
              let width, height;
              while ((matchWidhHeight = regex.exec(url)) !== null) {
                if (matchWidhHeight[1]) width = matchWidhHeight[1];   // nhóm width
                if (matchWidhHeight[2]) height = matchWidhHeight[2]; // nhóm height
              }
              ratio = Math.round((height / width) * 100000) / 1000;
            }
            else{
              if(ytList.indexOf(id) < 0){
                if(url.indexOf("width") < 0 && url.indexOf("height") < 0){
                  ytList.push(id);
                  ApiService.get(`/posts/youtube/${id}`)
                  .then(dataYT => {
                    console.log(dataYT);
                    if (dataYT && dataYT.data.width && dataYT.data.height) {
                      let data = editor.getData();
                      let separator = url.includes("?") ? "&" : "?";
                      data = data.replace(url, url + separator + 'width=' + dataYT.data.width + '&height=' + dataYT.data.height);
                      ratio = Math.round((dataYT.data.height / dataYT.data.width) * 100000) / 1000;
                      console.log(data);
                      data = data.replace(`<div id="${id}_player" style="position: relative; height: 0; padding-bottom: 56.2493%;">`, `<div style="position: relative; height: 0; padding-bottom: ${ratio}%;">`)
                      console.log(data);
                      editor.setData(data);
                      let el = document.getElementById(`${id}_player`);
                      if(el){
                        el.style.paddingBottom = `${ratio}%`;
                      }
                    }
                  })
                  .catch((e) => console.error(e));
                }
              }
            }
            
            return (
              `<div id="${id}_player" style="position: relative; height: 0; padding-bottom: ${ratio}%;">` +
              `<iframe src="https://www.youtube.com/embed/${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
              "</iframe>" +
              "</div>"
            );
          },
        },

        {
          name: "vimeo",
          url: [
            /^vimeo\.com\/(\d+)/,
            /^vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
            /^vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
            /^vimeo\.com\/channels\/[^/]+\/(\d+)/,
            /^vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
            /^vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
            /^player\.vimeo\.com\/video\/(\d+)/,
          ],
          html: (match) => {
            const id = match[1];

            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
              `<iframe src="https://player.vimeo.com/video/${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen>' +
              "</iframe>" +
              "</div>"
            );
          },
        },

        {
          name: "instagram",
          url: /^instagram\.com\/p\/(\w+)/,
        },
        {
          name: "twitter",
          url: /^twitter\.com/,
        },
        {
          name: "googleMaps",
          url: /^google\.com\/maps/,
        },
        {
          name: "flickr",
          url: /^flickr\.com/,
        },
        {
          name: "facebook-video",
          url: [
            /(https:\/\/www\.facebook\.com\/([\S]*)\/videos\/([0-9]*))/,
            /(https:\/\/www\.facebook\.com\/watch\/\?v=(([0-9]*)))/,
          ],
          html: (match) => {
            const id = match[3];

            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0;">' +
              '<iframe src="https://www.facebook.com/plugins/video.php?show_text=false&href=' +
              `https://www.facebook.com/watch/?v=${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" allow="encrypted-media">' +
              "</iframe>" +
              "</div>"
            );
          },
        },
        {
          name: "facebook",
          url: /^facebook\.com/,
        },
        {
          name: "vlive",
          url: [
            /^https:\/\/www.vlive.tv\/embed\/(\d+)/,
            /^https:\/\/www.vlive.tv\/video\/(\d+)/,
          ],
          html: (match) => {
            const id = match[1];
            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
              `<iframe src="https://www.vlive.tv/embed/${id}" ` +
              'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
              'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
              "</iframe>" +
              "</div>"
            );
          },
        },
        {
          name: "html5video",
          url: /\/[\w-]+.(m|M)(p|P)4/,
          html: (match) => {
            // console.log(match);
            const url = match["input"];
            const duration = url.match(/\?duration=(.*)/);
            // console.log(url)
            // console.log(duration);
            if((!video.src || video.src != url) && !duration){
              video.src = url;
              video.onloadedmetadata = (e) => {
                console.log("loadvideo");
                console.log(video);
                // console.log(audio.duration * 1000);
                // console.log(editor);
                let data = editor.getData();
                // console.log(data)
                data = data.replace('"' + url + '"','"' + url + '?duration=' + video.duration * 1000 + '"');
                editor.setData(data);
              }
            }
            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
              `<video style="position:absolute; height: 100%; width: 100%" controls>
									<source src="${url}" type="video/mp4">
									Your browser does not support HTML5 video.
								</video>` +
              "</div>"
            );
          },
        },
        {
          name: "html5audio",
          url: /\/[\w-]+.(m|M)(p|P)3/,
          html: (match) => {
            // console.log(match);
            const url = match["input"];
            const duration = url.match(/\?duration=(.*)/);
            // console.log(url)
            // console.log(duration);
            if((!audio.src || audio.src != url) && !duration){
              audio.src = url;
              audio.onloadedmetadata = (e) => {
                console.log("loadaudio");
                // console.log(this);
                // console.log(audio.duration * 1000);
                // console.log(editor);
                let data = editor.getData();
                // console.log(data)
                data = data.replace('"' + url + '"','"' + url + '?duration=' + audio.duration * 1000 + '"');
                editor.setData(data);
              }
            }
            
            return (
              '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 8%;">' +
              `<audio style="position:absolute; height: 100%; width: 100%" controls>
                  <source src="${url}" type="audio/mpeg">
                  Your browser does not support HTML5 audio.
                </audio>` +
              "</div>"
            );
          },
        },
      ],
      previewsInData: true,
    });

    /**
     * The media registry managing the media providers in the editor.
     *
     * @member {module:media-embed/mediaregistry~MediaRegistry} #registry
     */
    this.registry = new MediaRegistry(
      editor.locale,
      editor.config.get("mediaEmbed")
    );
  }

  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const schema = editor.model.schema;
    const t = editor.t;
    const conversion = editor.conversion;
    const renderMediaPreview = editor.config.get("mediaEmbed.previewsInData");

    const registry = this.registry;

    editor.commands.add("mediaEmbed", new MediaEmbedCommand(editor));

    // Configure the schema.
    schema.register("media", {
      isObject: true,
      isBlock: true,
      allowWhere: "$block",
      allowAttributes: ["url"],
    });

    // Model -> Data
    conversion.for("dataDowncast").elementToElement({
      model: "media",
      view: (modelElement, viewWriter) => {
        const url = modelElement.getAttribute("url");

        return createMediaFigureElement(viewWriter, registry, url, {
          renderMediaPreview: url && renderMediaPreview,
        });
      },
    });

    // Model -> Data (url -> data-oembed-url)
    conversion.for("dataDowncast").add(
      modelToViewUrlAttributeConverter(registry, {
        renderMediaPreview,
      })
    );

    // Model -> View (element)
    conversion.for("editingDowncast").elementToElement({
      model: "media",
      view: (modelElement, viewWriter) => {
        const url = modelElement.getAttribute("url");
        const figure = createMediaFigureElement(viewWriter, registry, url, {
          renderForEditingView: true,
        });

        return toMediaWidget(figure, viewWriter, t("media widget"));
      },
    });

    // Model -> View (url -> data-oembed-url)
    conversion.for("editingDowncast").add(
      modelToViewUrlAttributeConverter(registry, {
        renderForEditingView: true,
      })
    );

    // View -> Model (data-oembed-url -> url)
    conversion
      .for("upcast")
      // Upcast semantic media.
      .elementToElement({
        view: {
          name: "oembed",
          attributes: {
            url: true,
          },
        },
        model: (viewMedia, modelWriter) => {
          const url = viewMedia.getAttribute("url");

          if (registry.hasMedia(url)) {
            return modelWriter.createElement("media", { url });
          }
        },
      })
      // Upcast non-semantic media.
      .elementToElement({
        view: {
          name: "div",
          attributes: {
            "data-oembed-url": true,
          },
        },
        model: (viewMedia, modelWriter) => {
          const url = viewMedia.getAttribute("data-oembed-url");

          if (registry.hasMedia(url)) {
            return modelWriter.createElement("media", { url });
          }
        },
      });
  }
}
